import React, { useState, useEffect } from 'react'
import { useLazyQuery } from '@apollo/client'
import { GET_MEALS_LIST } from 'graphql/common/queries'
import { Input, InputGroup } from 'components/input'
import {
    MagnifyingGlassIcon,
    EllipsisVerticalIcon,
    ArrowLeftIcon,
    Squares2X2Icon,
    PencilIcon,
    EyeIcon,
} from '@heroicons/react/16/solid'
import { t } from 'i18next'
import {
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
} from 'components/dropdown'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/table'
import { Divider } from 'components/divider'
import { Pagination, PaginationList, PaginationNext, PaginationPage, PaginationPrevious } from 'components/pagination'
import loadingImage from '../../assets/images/boat-loading.gif'
import { ErrorDialog, SuccessDialog, ConfirmDialog } from 'components/custom/ResponseDialog'
import { useNavigate } from 'react-router-dom'
import { getPageRange } from 'utils/paginationHelper'

export default function Meals() {
    const navigate = useNavigate()

    const [searchString, setSearchString] = useState('')
    const [isSearch, setIsSearch] = useState(false)

    const [mealList, setMealList] = useState(null)
    const [mealCount, setMealCount] = useState(0)

    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const totalPages = Math.ceil(mealCount / rowsPerPage)

    const [getMealsList, { loading: mealsListLoading, error: mealsListError }] = useLazyQuery(GET_MEALS_LIST, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data.searchMeal.success && data.searchMeal.code === '200') {
                setMealList(data.searchMeal.meals)
                setMealCount(data.searchMeal.total_count || data.searchMeal.meals.length)
            }
        },
    })

    const searchMeals = searchKey => {
        getMealsList({
            variables: {
                page: {
                    limit: rowsPerPage,
                    offset: page * rowsPerPage,
                },
                name: searchKey || '',
            },
        })
    }

    useEffect(() => {
        searchMeals(searchString)
    }, [page, rowsPerPage])

    const handlePageChange = newPage => {
        setPage(newPage)
    }

    const onClickSearch = () => {
        setPage(0)
        setRowsPerPage(10)
        searchMeals(searchString)
    }

    const onClickSearchClear = () => {
        if (searchString == '') 
            return;
        setSearchString('')
        setPage(0)
        setRowsPerPage(10)
        searchMeals('')
    }

    const formatPrice = (base, tax) => {
        const total = parseFloat(base) + parseFloat(tax)
        return `${base} + ${tax} = ${total.toFixed(2)}`
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => {
                        navigate('/')
                    }}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <div>
                        <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap justify-between">
                            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                                <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1] w-[40px] h-[40px]">
                                        <Squares2X2Icon className="w-[20px] h-[20px] fill-[#285f74]" />
                                    </div>
                                    <div>
                                        <span className="ms-3"> {t('Manage Meals')}</span>
                                        <p className="ms-3 text-sm font-normal leading-6 weight-[200] text-gray-500">
                                            {t('Easily view, add, and edit meal details.')}
                                        </p>
                                    </div>
                                </h1>
                            </div>
                            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap sm:text-end">
                                <button
                                    onClick={() => navigate('/meals/meal-form')}
                                    type="button"
                                    className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                                >
                                    {t('Add Meal')}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="mt-8 mb-4 flex flex-col sm:flex-row flex-wrap max-w-xl gap-4">
                        <div className="flex-2">
                            <InputGroup>
                                <MagnifyingGlassIcon />
                                <Input
                                    value={searchString}
                                    onChange={e => setSearchString(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                            onClickSearch();
                                        } else if (e.key === 'Escape') {
                                            onClickSearchClear();
                                        }
                                    }}
                                    name="search"
                                    id="search"
                                    placeholder={t('Search meals')}
                                />
                            </InputGroup>
                        </div>

                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={onClickSearch}
                                type="button"
                                className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                            >
                                {t('Search')}
                            </button>
                            <button
                                onClick={onClickSearchClear}
                                type="button"
                                className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            >
                                {t('Clear')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <Divider />

            {mealsListLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                </div>
            )}

            {mealsListError && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to fetch meals!')}</div>
                </div>
            )}

            {!mealsListLoading && !mealsListError && mealList?.length === 0 && (
                <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="mx-auto h-12 w-12 text-gray-400"
                    >
                        <path
                            d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No data found!')}</h3>
                    <p className="mt-1 text-sm text-gray-500">{t('Try adjusting your search criteria')}</p>
                    <div className="mt-6"></div>
                </div>
            )}

            {!mealsListLoading && !mealsListError && mealList?.length > 0 && (
                <>
                    <div className="table-custom overflow-x-scroll lg:overflow-hidden rounded-b-lg bg-white p-0 flex">
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{t('English Description')}</TableHeader>
                                    <TableHeader>{t('Arabic Description')}</TableHeader>
                                    <TableHeader>{t('Price (Base)')}</TableHeader>
                                    <TableHeader>{t('Tax')}</TableHeader>
                                    <TableHeader>{t('Total Price')}</TableHeader>
                                    <TableHeader>{t('Status')}</TableHeader>
                                    <TableHeader>{t('Actions')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {mealList.map(meal => (
                                    <TableRow
                                        key={meal.meal_id}
                                        className="cursor-pointer hover:bg-gray-50"
                                        onClick={() => navigate(`/meals/meal-details/${meal.meal_id}`)}
                                        title={`Meal #${meal.meal_id}`}
                                    >
                                        <TableCell>{meal.description.en}</TableCell>
                                        <TableCell>{meal.description.ar}</TableCell>
                                        <TableCell>{meal.price.base}</TableCell>
                                        <TableCell>{meal.price.tax}</TableCell>
                                        <TableCell>
                                            {(parseFloat(meal.price.base) + parseFloat(meal.price.tax)).toFixed(2)}
                                        </TableCell>
                                        <TableCell>
                                            <span
                                                className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                                                    meal.active
                                                        ? 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20'
                                                        : 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20'
                                                }`}
                                            >
                                                {meal.active ? t('Active') : t('Inactive')}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <Dropdown>
                                                <DropdownButton
                                                    plain
                                                    aria-label="More options"
                                                    onClick={e => e.stopPropagation()}
                                                    className="cursor-pointer"
                                                >
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom">
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(`/meals/meal-details/${meal.meal_id}`)
                                                        }}
                                                    >
                                                        <EyeIcon />
                                                        <DropdownLabel>{t('View')}</DropdownLabel>
                                                    </DropdownItem>
                                                    <DropdownDivider />
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(`/meals/meal-form/${meal.meal_id}`)
                                                        }}
                                                    >
                                                        <PencilIcon />
                                                        <DropdownLabel>{t('Edit')}</DropdownLabel>
                                                    </DropdownItem>
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <Divider />
                    <Pagination className="py-6">
                        <PaginationPrevious
                            onClick={() => handlePageChange(page > 0 ? page - 1 : 0)}
                            disabled={page === 0}
                        />
                        <PaginationList>
                            {getPageRange(page, totalPages).map(index => (
                                <PaginationPage
                                    key={index}
                                    current={index === page}
                                    onClick={() => handlePageChange(index)}
                                >
                                    {index + 1}
                                </PaginationPage>
                            ))}
                        </PaginationList>
                        <PaginationNext
                            onClick={() => handlePageChange(page < totalPages - 1 ? page + 1 : totalPages - 1)}
                            disabled={page === totalPages - 1}
                        />
                    </Pagination>
                    <Divider />
                </>
            )}
        </>
    )
}
